import {
  Box, TickIcon, Text, SuccessIcon,
} from '@myob/myob-widgets';
import React from 'react';
import { FEELIX_THEMES } from '../../../constants';
import modernStyles from './styles/FeatureList.modern.module.scss';
import partnerStyles from './styles/FeatureList.partner.module.scss';
import soloStyles from './styles/FeatureList.solo.module.scss';
import commonStyles from './styles/FeatureList.common.module.scss';

interface FeatureListProps {
  items: string[];
  theme: string;
}

const getFeatureListConfig = (theme: string) => {
  switch (theme) {
    case FEELIX_THEMES.SOLO:
      return {
        styles: soloStyles,
        featureRowMarginBottom: 'sm' as const,
        featureTextFontSize: 'sm' as const
      };
    case FEELIX_THEMES.PARTNER:
      return {
        styles: partnerStyles,
        featureRowMarginBottom: 'md' as const,
        featureTextFontSize: 'md' as const
      };
    case FEELIX_THEMES.MODERN:
      return {
        styles: modernStyles,
        featureRowMarginBottom: 'md' as const,
        featureTextFontSize: 'md' as const
      };
    case FEELIX_THEMES.COMMON:
      return {
        styles: commonStyles,
        featureRowMarginBottom: 'clear' as const,
        featureTextFontSize: 'sm' as const
      };
    default:
      return {
        styles: modernStyles,
        featureRowMarginBottom: 'md' as const,
        featureTextFontSize: 'md' as const
      };
  }
};

const TICK_ICON_THEMES = new Set<string>([
  FEELIX_THEMES.SOLO,
  FEELIX_THEMES.PARTNER,
  FEELIX_THEMES.COMMON
]);

export const FeatureList: React.FC<FeatureListProps> =
  ({
    items,
    theme
  }): React.JSX.Element => {
    const {
      styles,
      featureRowMarginBottom,
      featureTextFontSize
    } = getFeatureListConfig(theme);
    const shouldRenderTickIcon = TICK_ICON_THEMES.has(theme);
    const shouldRenderSuccessIcon = theme === FEELIX_THEMES.MODERN;
    return (
      <ul
        className={styles.featureListContainer}
        data-testid='feature-list-container'
      >
        {items.map((feature) => (
          <li
            key={feature}
            className={styles.featureItem}
            data-testid='feature-item'
          >
            <Box
              alignItems='flex-start'
              display='flex'
              gap='2xs'
              color='neutralStrongest'
              marginBottom={featureRowMarginBottom}
            >
              <div
                className={styles.successIcon}
                data-testid='success-icon'
              >
                {shouldRenderTickIcon && <TickIcon color='brand' size='sm' />}
                {shouldRenderSuccessIcon && <SuccessIcon color='currentColor' size='lg' />}
              </div>
              <Text
                as='span'
                tone='neutralStrongest'
                fontSize={featureTextFontSize}
              >
                {feature}
              </Text>
            </Box>
          </li>
        ))}
      </ul>
    );
  };
